import React from 'react';
import { motion } from 'framer-motion';
import { Zap, CheckCircle, Settings, Wrench, Gauge } from 'lucide-react';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import actuatorImage from '../../../images/herosection/actuators.jpg';
import actuator1 from '../../../images/Actuator/PneumaticActuator1.png';
import actuator2 from '../../../images/Actuator/ElectricActuator1.png';
import actuator3 from '../../../images/Actuator/SmartPositioner1.png';
import EnquiryForm from '../../../components/EnquiryForm/EnquiryForm';
import './ActuatorService.css';

const ActuatorService = () => {
  const features = [
    'Pneumatic Actuators',
    'Electric Actuators',
    'Smart positioners',
    'Troubleshooting',
    'Limit setting',
    'Routine inspections and diagnostics',
    'Preventive maintenance programs',
    'Reconditioning/refurbishment work',
    'Genuine spare parts inventory',
    'Emergency repair services'
  ];

  const services = [
    {
      title: 'Electric Actuators',
      description: 'Comprehensive maintenance and repair services for electric actuators',
      icon: <Zap className="w-8 h-8" />,
      details: [
        'Motor and gearbox maintenance',
        'Limit switch calibration',
        'Position feedback systems',
        'Electrical component testing'
      ]
    },
    {
      title: 'Pneumatic Actuators',
      description: 'Expert services for pneumatic actuator systems',
      icon: <Settings className="w-8 h-8" />,
      details: [
        'Cylinder and piston maintenance',
        'Air supply system optimization',
        'Positioner calibration',
        'Seal and gasket replacement'
      ]
    },
    {
      title: 'Smart positioners',
      description: 'Calibration, configuration and support for smart positioners',
      icon: <Gauge className="w-8 h-8" />,
      details: [
        'Configuration and calibration',
        'Diagnostics and troubleshooting',
        'Limit setting',
        'Integration with control systems'
      ]
    },
    {
      title: 'Preventive Maintenance',
      description: 'Scheduled maintenance to prevent unexpected failures',
      icon: <Wrench className="w-8 h-8" />,
      details: [
        'Regular inspection schedules',
        'Performance monitoring',
        'Predictive maintenance',
        'Documentation and reporting'
      ]
    }
  ];


  return (
    <div className="actuator-service-page">
      {/* Hero Section */}
      <section className="actuator-hero" style={{ backgroundImage: `url(${actuatorImage})` }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="actuator-hero-content"
          >
            <Breadcrumb />
            <h1 className="actuator-hero-title">
              Electric & Pneumatic <span className="actuator-hero-title-highlight">Actuator Maintenance</span>
            </h1>
            <p className="actuator-hero-description">
              Expert maintenance and repair services for electric and pneumatic actuators. 
              Ensuring reliable operation and optimal performance of your control systems.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="actuator-overview">
        <div className="container-custom">
          <div className="actuator-overview-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="actuator-overview-content"
            >
              <h2 className="actuator-overview-title">Comprehensive Actuator Solutions</h2>
              <p className="actuator-overview-description">
                Our team of highly skilled technicians specializes in the maintenance of electric and pneumatic actuators. 
                We offer routine inspections, diagnostics, and preventive maintenance to keep your systems running at peak performance.
              </p>
              <div className="actuator-features">
                {features.map((feature, index) => (
                  <div key={index} className="actuator-feature-item">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="actuator-overview-visual"
            >
              <div className="actuator-image-gallery">
                {[
                  { src: actuator1, alt: 'Actuator Service', title: 'Expert Maintenance' },
                  { src: actuator2, alt: 'Actuator Repair', title: 'Professional Repair' },
                  { src: actuator3, alt: 'Actuator Quality', title: 'Quality Service' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="actuator-image-card"
                  >
                    <div className="actuator-image-card-inner">
                      <img src={item.src} alt={item.alt} />
                      <div className="actuator-image-card-overlay">
                        <h4>{item.title}</h4>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="actuator-services">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="actuator-services-header"
          >
            <h2 className="actuator-services-title">Our Actuator Services</h2>
            <p className="actuator-services-description">
              Specialized services for different types of actuators
            </p>
          </motion.div>
          
          <div className="actuator-services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="actuator-service-card"
              >
                <div className="actuator-service-icon">
                  <div className="actuator-service-icon-wrapper">
                    {service.icon}
                  </div>
                </div>
                <h3 className="actuator-service-title">{service.title}</h3>
                <p className="actuator-service-description">{service.description}</p>
                <ul className="actuator-service-details">
                  {service.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="actuator-service-detail-item">
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section - Generic */}
      <section className="actuator-brands">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="actuator-brands-header"
          >
            <h2 className="actuator-brands-title">We Work With All Brands</h2>
            <p className="actuator-brands-description">
              We service all major actuator and positioner manufacturers. Our team has the expertise to maintain and repair equipment from any brand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="actuator-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="actuator-cta-content"
          >
            <h2 className="actuator-cta-title">Need Actuator Maintenance?</h2>
            <p className="actuator-cta-description">
              Get a free consultation and quote by filling out the form below
            </p>
            
            {/* Enquiry Form */}
            <div className="actuator-cta-form">
              <EnquiryForm serviceName="Actuator" />
            </div>
            
            {/*  */}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ActuatorService;
